import {
  checkDns,
  getEndpointText,
  renewEndpoint,
  selectEndpointById,
} from "@app/deploy";
import { useDispatch, useSelector } from "@app/react";
import { useLoader, useLoaderSuccess, useQuery } from "@app/react";
import { endpointDetailActivityUrl } from "@app/routes";
import type { AcmeChallenge, DeployEndpoint } from "@app/types";
import { useNavigate, useParams } from "react-router";
import {
  Box,
  Button,
  CopyText,
  Group,
  IconCheck,
  IconX,
  Tooltip,
  tokens,
} from "../shared";

const isWildcard = (url: string) => {
  return url.startsWith("*");
};

const CnameItem = ({ from, to }: { from: string; to: string }) => {
  const loader = useQuery(checkDns({ from, to }));
  const onCheck = () => {
    loader.trigger();
  };

  return (
    <div className="flex justify-between items-center gap-2 border p-4 rounded-lg">
      <Group size="sm">
        <div className="font-semibold">CNAME</div>
        <Group variant="horizontal" size="sm">
          <div className="w-[50px] font-bold">From</div>
          <CopyText text={from} />
        </Group>
        <Group variant="horizontal" size="sm">
          <div className="w-[50px] font-bold">To</div>
          <CopyText text={to} />
        </Group>
      </Group>
      <div className="flex items-center gap-2">
        <div>
          {loader.meta.success ? (
            <Tooltip text="DNS check succeeded">
              <IconCheck color="#00633F" />
            </Tooltip>
          ) : (
            <Tooltip text="DNS check failed">
              <IconX color="#AD1A1A" />
            </Tooltip>
          )}
        </div>
        <Button onClick={onCheck} isLoading={loader.isLoading}>
          Check
        </Button>
      </div>
    </div>
  );
};

const WildcardDnsInstructions = ({
  domain,
  targetHost,
}: { domain: string; targetHost: string }) => (
  <div className="border p-4 rounded-lg bg-yellow-50 border-yellow-200">
    <div className="flex items-start gap-3">
      <div className="text-yellow-600 mt-1">⚠️</div>
      <div>
        <div className="font-semibold text-yellow-800 mb-2">
          Wildcard Domain Configuration
        </div>
        <p className="text-sm text-yellow-700 mb-3">
          Since you're using a wildcard domain (
          <code className="bg-yellow-100 px-1 rounded">{domain}</code>), you'll
          need to create CNAME records for each specific subdomain you want to
          use for this endpoint.
        </p>
        <div className="text-sm text-yellow-700">
          <strong>Example:</strong> For subdomains like{" "}
          <code className="bg-yellow-100 px-1 rounded">
            api.{domain.slice(2)}
          </code>{" "}
          or{" "}
          <code className="bg-yellow-100 px-1 rounded">
            app.{domain.slice(2)}
          </code>
          , create:
        </div>
        <div className="mt-2 space-y-1 text-sm font-mono bg-yellow-100 p-2 rounded">
          <div>
            api.{domain.slice(2)} → {targetHost}
          </div>
          <div>
            app.{domain.slice(2)} → {targetHost}
          </div>
          <div className="text-yellow-600">
            ...any other subdomains you need
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DnsRoutingSection = ({ enp }: { enp: DeployEndpoint }) => {
  const txt = getEndpointText(enp);
  const targetHost = txt.hostname;
  const isWildcardDomain = isWildcard(enp.userDomain);

  // Show for all endpoints with a custom domain
  if (
    !enp.acme ||
    !enp.userDomain ||
    !targetHost ||
    targetHost === "New Endpoint"
  ) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className={tokens.type.h3}>DNS Routing Configuration</h3>

      <div className="mb-4">
        {isWildcardDomain ? (
          <p className="mb-2">
            For wildcard domains, you need to create CNAME records for each
            specific subdomain you want to use to route traffic to this
            endpoint.
          </p>
        ) : (
          <p className="mb-2">
            To configure your custom domain, you need to create CNAME record(s)
            to route traffic from your custom domain to this endpoint.
          </p>
        )}
        <p className="text-sm text-gray-600">
          {isWildcardDomain ? (
            <>
              This is in addition to the SSL certificate validation records
              below.
            </>
          ) : (
            <>
              This is separate from the SSL certificate validation records below
              and is required for your domain to actually reach your
              application.
            </>
          )}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {isWildcardDomain ? (
          <WildcardDnsInstructions
            domain={enp.userDomain}
            targetHost={targetHost}
          />
        ) : (
          <CnameItem from={enp.userDomain} to={targetHost} />
        )}
      </div>
    </div>
  );
};

const ChallengeItem = ({ challenge }: { challenge: AcmeChallenge }) => {
  return (
    <div>
      {challenge.to.map((to) => {
        if (to.legacy) {
          return null;
        }

        return (
          <CnameItem
            key={`${challenge.from.name}-${to.name}`}
            from={challenge.from.name}
            to={to.name}
          />
        );
      })}
    </div>
  );
};

export const EndpointDetailSetupPage = () => {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enp = useSelector((s) => selectEndpointById(s, { id }));
  const challenges = enp.acmeConfiguration?.challenges || [];
  const action = renewEndpoint({ id });
  const renew = () => {
    dispatch(action);
  };
  const loader = useLoader(action);
  useLoaderSuccess(loader, () => {
    navigate(endpointDetailActivityUrl(enp.id));
  });

  return (
    <Box>
      <div className="flex flex-col gap-2">
        <DnsRoutingSection enp={enp} />

        <h3 className={tokens.type.h3}>Managed HTTPS Validation Records</h3>

        <div>
          {enp.internal ? (
            <>
              In addition to the DNS routing record above, create the following
              CNAME records for SSL certificate validation via the DNS provider
              you use for <strong>{enp.userDomain}</strong>. These records
              enable automated SSL certificate provisioning using the DNS-01
              challenge method.
            </>
          ) : (
            <>
              Create the following CNAME records via the DNS provider you use
              for <strong>{enp.userDomain}</strong>. These records enable SSL
              certificate validation.
            </>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {challenges.map((challenge) => (
            <ChallengeItem key={challenge.from.name} challenge={challenge} />
          ))}
        </div>

        <div>
          Once you've done so, click the "I created the records" button, and
          Aptible will attempt to generate a new SSL certificate for your
          domain.
        </div>

        <div>
          {enp.acmeStatus === "pending" ? (
            <p>
              Note: you have not provided a transitional certificate. Your app
              will be temporarily unavailable once you switch over DNS.
            </p>
          ) : null}

          {enp.acmeStatus === "transitioning" ? (
            <p>
              Note: you have provided a transitional certificate. Your app will
              be available once you switch over DNS, and Managed HTTPS setup
              will happen in the background.
            </p>
          ) : null}
        </div>

        <Button
          className="w-[200px]"
          isLoading={loader.isLoading}
          onClick={renew}
        >
          I created the records
        </Button>
      </div>
    </Box>
  );
};
